/**
 * 班级 API（class-management change 5-1，ADM-16）。
 *
 * 对齐后端 classes BC 的 REST 契约：
 * - GET /classes -> Paginated<ClassListItem>（筛选 + 分页 + 派生容量）
 * - GET /classes/:id -> ClassDetail
 * - POST /classes -> ClassDetail（新建，DRAFT 初始）
 * - PATCH /classes/:id -> ClassDetail（编辑，乐观锁 version；校区/SKU 不可改）
 * - POST /classes/:id/complete -> ClassDetail（结课，二次确认）
 * - DELETE /classes/:id -> { classId }（软删除 -> ARCHIVED，三重阻断 409；
 *   仅授权角色可用，409 错误码 classes.class_has_active_students /
 *   classes.class_has_active_schedules / classes.class_has_scheduled_sessions /
 *   classes.completed_class_not_deletable）
 * - GET /classes/:id/members -> ClassMember[]（按学生去重显示最终状态）
 * - POST /classes/:id/members -> EnrollResult（单个添加成员，四步校验链）
 * - DELETE /classes/:id/members/:studentId -> RemoveResult（移除成员软删除）
 * - GET /classes/by-student/:studentId -> StudentClassView[]（按学生查班级，
 *   含 ENDED 历史班级，student-detail-cross-domain-tabs 学生详情浮层）
 *
 * 路径从 api.config.ts 导入，禁止内联；Transport 统一解包 `{success, data}` 信封。
 */
import { ApiPaths } from './api.config';
import { http } from './transport';

/** 班级状态五态（一期 API 暴露 DRAFT/COMPLETED 流转，其余预留）。 */
export type ClassStatus =
  | 'DRAFT'
  | 'RECRUITING'
  | 'ACTIVE'
  | 'COMPLETED'
  | 'ARCHIVED';

/** 在班状态。 */
export type EnrollmentStatus = 'ACTIVE' | 'ENDED';

/** 班级列表/详情视图（对齐后端 ClassListItemDto/ClassDetailDto）。 */
export interface ClassListItem {
  id: string;
  name: string;
  campusId: string;
  campusName: string;
  skuId: string;
  skuName: string;
  teacherId: string;
  teacherName: string;
  defaultClassroomId: string;
  defaultClassroomName: string;
  capacity: number;
  /** 有效在班人数（派生值） */
  activeCount: number;
  /** 剩余容量 = capacity - activeCount（派生值） */
  remaining: number;
  /** 是否已满（派生值） */
  isFull: boolean;
  startDate: string;
  endDate: string;
  status: ClassStatus;
  /** 下次课时间（一期占位 null，待 5-4 回填） */
  nextSessionTime: string | null;
  /** 乐观锁版本号 */
  version: number;
}

/** 成员名单项（按学生去重显示最终状态）。 */
export interface ClassMember {
  studentId: string;
  studentName: string;
  studentNo: string;
  /** 最近一次入班时间 */
  joinedAt: string;
  status: EnrollmentStatus;
  /** 最近一次离班时间（ACTIVE 时 null） */
  leftAt: string | null;
  sourceApplicationId: string | null;
}

/** 分页响应。 */
export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** 按学生查班级视图（对齐后端 StudentClassDto，含 ENDED 历史班级）。 */
export interface StudentClassView {
  classId: string;
  className: string;
  campusId: string;
  campusName: string;
  skuId: string;
  skuName: string;
  teacherId: string | null;
  /** 班级现任教师姓名快照（未指定教师时为 null）。 */
  teacherName: string | null;
  joinedAt: string;
  /** 最终状态为 ENDED 时为最近一次离班时间，否则为 null。 */
  leftAt: string | null;
  enrollmentStatus: EnrollmentStatus;
}

/** 班级列表查询参数（筛选 + 分页）。 */
export interface ClassListQuery {
  name?: string;
  campusId?: string;
  skuId?: string;
  teacherId?: string;
  status?: ClassStatus;
  /** 排除已结束班级（服务端按终态集合过滤，与排课发布校验同源）。 */
  excludeEnded?: boolean;
  /** 排除已删除班级（仅排 ARCHIVED 保留 COMPLETED 可见，班级管理列表默认启用）。 */
  excludeArchived?: boolean;
  startDateFrom?: string;
  startDateTo?: string;
  page?: number;
  pageSize?: number;
}

/** 新建班级请求体。 */
export interface CreateClass {
  campusId: string;
  skuId: string;
  name: string;
  capacity: number;
  teacherId: string;
  defaultClassroomId: string;
  startDate: string;
  endDate: string;
}

/** 编辑班级请求体（乐观锁；校区/SKU 不可改）。 */
export interface UpdateClass {
  name?: string;
  capacity?: number;
  teacherId?: string;
  defaultClassroomId?: string;
  /** 乐观锁版本号（对齐后端 UpdateClassDto.expectedVersion） */
  expectedVersion: number;
}

/** 添加成员结果。 */
export interface EnrollResult {
  enrollmentId: string;
  applied: boolean;
}

/** 移除成员结果。 */
export interface RemoveResult {
  applied: boolean;
}

function buildListQuery(query: ClassListQuery): string {
  const params = new URLSearchParams();
  if (query.name) params.set('name', query.name);
  if (query.campusId) params.set('campusId', query.campusId);
  if (query.skuId) params.set('skuId', query.skuId);
  if (query.teacherId) params.set('teacherId', query.teacherId);
  if (query.status) params.set('status', query.status);
  if (query.excludeEnded) params.set('excludeEnded', 'true');
  if (query.excludeArchived) params.set('excludeArchived', 'true');
  if (query.startDateFrom) params.set('startDateFrom', query.startDateFrom);
  if (query.startDateTo) params.set('startDateTo', query.startDateTo);
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  const qs = params.toString();
  return qs ? `${ApiPaths.classes.list}?${qs}` : ApiPaths.classes.list;
}

async function listClasses(
  query: ClassListQuery = {},
): Promise<Paginated<ClassListItem>> {
  return http.get<Paginated<ClassListItem>>(buildListQuery(query));
}

async function getClass(id: string): Promise<ClassListItem> {
  return http.get<ClassListItem>(ApiPaths.classes.detail(id));
}

async function createClass(dto: CreateClass): Promise<ClassListItem> {
  return http.post<ClassListItem>(ApiPaths.classes.create, dto);
}

async function updateClass(
  id: string,
  dto: UpdateClass,
): Promise<ClassListItem> {
  return http.patch<ClassListItem>(ApiPaths.classes.update(id), dto);
}

async function completeClass(id: string): Promise<ClassListItem> {
  return http.post<ClassListItem>(ApiPaths.classes.complete(id), {
    confirmed: 'true',
  });
}

/** 软删除结果（服务端返回被删班级 ID）。 */
export interface DeleteResult {
  classId: string;
}

/** 软删除班级（-> ARCHIVED；被引用时服务端 409，错误码见文件头注释）。 */
async function deleteClass(id: string): Promise<DeleteResult> {
  return http.delete<DeleteResult>(ApiPaths.classes.remove(id));
}

async function listMembers(id: string): Promise<ClassMember[]> {
  return http.get<ClassMember[]>(ApiPaths.classes.members(id));
}

/** 按学生查班级（学生详情浮层班级 Tab：含 ENDED 历史班级，joinedAt 倒序）。 */
async function listClassesByStudent(studentId: string): Promise<StudentClassView[]> {
  const res = await http.get<{ items: StudentClassView[] }>(
    ApiPaths.classes.byStudent(studentId),
  );
  return res.items ?? [];
}

async function enrollMember(
  id: string,
  studentId: string,
): Promise<EnrollResult> {
  // 幂等键：学生+班级稳定组合（重复提交不重复入班）
  const idempotencyKey = `enroll-${studentId}-${id}`;
  return http.post<EnrollResult>(ApiPaths.classes.members(id), {
    studentId,
    idempotencyKey,
  });
}

async function removeMember(
  id: string,
  studentId: string,
): Promise<RemoveResult> {
  return http.delete<RemoveResult>(
    ApiPaths.classes.removeMember(id, studentId),
  );
}

/** 班级 API 唯一对外入口。 */
export const classesApi = {
  listClasses,
  getClass,
  createClass,
  updateClass,
  completeClass,
  deleteClass,
  listMembers,
  listClassesByStudent,
  enrollMember,
  removeMember,
};
